import { FC } from "react";
import styled, { css } from "styled-components";

import { hexToRgba } from "@/utils/theme";
import { Thumbnail } from "./Table";

const ThumbOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: ${({ theme }) => hexToRgba(theme.colors.black, 0.5)};
  display: flex;
  justify-content: center;
  align-items: center;
  opacity: 0;
  transition: opacity ease-out 0.2s;
`;

const ThumbHolder = styled.div<{ $enlarged?: boolean }>`
  width: 92px;
  height: 52px;
  position: relative;
  flex-shrink: 0;
  ${({ $enlarged }) =>
    $enlarged &&
    css`
      width: 276px;
      height: 156px;
    `}
  &:hover {
    ${ThumbOverlay} {
      opacity: 1;
    }
  }
`;

const EnlargeButton = styled.button<{ $enlarged?: boolean }>`
  background-color: ${({ theme }) => hexToRgba(theme.colors.white, 0.5)};
  border-radius: 4px;
  height: 24px;
  width: 24px;
  display: flex;
  justify-content: center;
  align-items: center;
  transition: opacity ease-out 0.2s;
  &:hover,
  &:active {
    opacity: 0.8;
  }
  svg {
    color: ${({ theme }) => theme.colors.white};
  }
  ${({ $enlarged }) =>
    $enlarged &&
    css`
      width: 40px;
      height: 40px;
    `}
`;

const StyledThumbnail = styled(Thumbnail)`
  width: 100% !important;
  margin: 0 !important;
  height: 100% !important;
`;

interface Props {
  areThumbsEnlarged: boolean;
  setAreThumbsEnlarged: React.Dispatch<React.SetStateAction<boolean>>;
  thumbnail?: string;
}

const ScalableThumbnail: FC<Props> = ({
  areThumbsEnlarged,
  setAreThumbsEnlarged,
  thumbnail,
}) => {
  return (
    <ThumbHolder $enlarged={areThumbsEnlarged}>
      <StyledThumbnail src={thumbnail} />
      <ThumbOverlay>
        <EnlargeButton
          $enlarged={areThumbsEnlarged}
          onClick={() => setAreThumbsEnlarged((prev) => !prev)}
        >
          {!areThumbsEnlarged ? (
            <svg
              width="12"
              height="12"
              viewBox="0 0 12 12"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <g clipPath="url(#clip0_6296_142904)">
                <path
                  d="M7.85742 1.35742H10.6431V4.14314"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M7.39258 4.60742L10.6426 1.35742"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M4.14314 10.6431H1.35742V7.85742"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M4.60742 7.39258L1.35742 10.6426"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </g>
              <defs>
                <clipPath id="clip0_6296_142904">
                  <rect width="12" height="12" fill="currentColor" />
                </clipPath>
              </defs>
            </svg>
          ) : (
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M9.87012 2.38965V6.12991H13.6104"
                stroke="white"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M14.2338 1.7666L9.87012 6.13024"
                stroke="white"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M2.38965 9.87012H6.12991V13.6104"
                stroke="white"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M1.76562 14.2338L6.12926 9.87012"
                stroke="white"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          )}
        </EnlargeButton>
      </ThumbOverlay>
    </ThumbHolder>
  );
};

export default ScalableThumbnail;
